import React, { useEffect, useState } from 'react';
import { X, Lock, Eye, EyeOff, Loader2, Check, ShieldAlert } from 'lucide-react';
import { api } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';

const ChangePasswordModal = ({ role = 'admin', onClose }) => {
  const { toast } = useToast();
  const [form, setForm] = useState({ current_password: '', new_password: '', confirm: '' });
  const [show, setShow] = useState({ c: false, n: false });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (form.new_password.length < 8) { toast({ title: 'Password too short', description: 'At least 8 characters.' }); return; }
    if (form.new_password !== form.confirm) { toast({ title: 'Passwords do not match' }); return; }
    setBusy(true);
    try {
      const endpoint = role === 'recruiter' ? '/recruiter/change-password' : '/admin/change-password';
      await api.post(endpoint, { current_password: form.current_password, new_password: form.new_password });
      toast({ title: 'Password updated', description: 'Your password has been changed successfully.' });
      onClose();
    } catch (err) {
      toast({ title: 'Update failed', description: err?.response?.data?.detail || 'Error' });
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} data-testid="change-password-backdrop" />
      <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl z-[61]">
        <div className="border-b border-[#0B5E2A]/10 px-6 py-4 flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-wider text-[#6b7e6d] font-semibold">Security</div>
            <h3 className="font-display text-xl text-[#0B5E2A]">Change Password</h3>
          </div>
          <button type="button" onClick={onClose} data-testid="change-password-close" className="p-2 rounded hover:bg-[#f0f6ee]"><X className="h-5 w-5 pointer-events-none" /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <div className="flex items-start gap-2.5 bg-[#DEE9FA] text-[#1e40af] rounded-lg p-3 text-sm">
            <ShieldAlert className="h-4 w-4 mt-0.5 shrink-0" />
            <span>Choose a strong password with at least 8 characters. You'll stay signed in on this device.</span>
          </div>
          <PasswordField label="Current Password" show={show.c} toggle={() => setShow({ ...show, c: !show.c })} value={form.current_password} onChange={onChange('current_password')} testid="current-password-input" />
          <PasswordField label="New Password" show={show.n} toggle={() => setShow({ ...show, n: !show.n })} value={form.new_password} onChange={onChange('new_password')} testid="new-password-input" />
          <PasswordField label="Confirm New Password" show={show.n} toggle={() => setShow({ ...show, n: !show.n })} value={form.confirm} onChange={onChange('confirm')} testid="confirm-password-input" />
          <div className="flex gap-2 pt-2 border-t border-[#0B5E2A]/10">
            <button type="button" onClick={onClose} data-testid="change-password-cancel" className="px-5 py-2.5 rounded-md border border-[#0B5E2A]/25 text-[#0B5E2A] font-semibold hover:bg-[#f0f6ee]">Cancel</button>
            <button type="submit" disabled={busy} data-testid="change-password-submit" className="flex-1 btn-green px-5 py-2.5 rounded-md inline-flex items-center justify-center gap-2 disabled:opacity-60">
              {busy ? (<><Loader2 className="h-4 w-4 animate-spin" /> Saving...</>) : (<><Check className="h-4 w-4 pointer-events-none" /> Update Password</>)}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const PasswordField = ({ label, value, onChange, show, toggle, testid }) => (
  <div>
    <label className="block text-sm font-medium text-[#2e4030] mb-1.5">{label}</label>
    <div className="relative">
      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#6b7e6d]" />
      <input type={show ? 'text' : 'password'} value={value} onChange={onChange} data-testid={testid} className="w-full pl-10 pr-10 py-2.5 rounded-md border border-[#0B5E2A]/20 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B5E2A]/30" />
      <button type="button" onClick={toggle} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6b7e6d] hover:text-[#0B5E2A]">
        {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  </div>
);

export default ChangePasswordModal;
